// src/components/store/VisualSearchModal.tsx
"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Camera,
  Upload,
  X,
  Sparkles,
  Loader2,
  Search,
  ShoppingBag,
  ArrowRight,
  Image as ImageIcon,
} from "lucide-react";

interface VisualSearchModalProps {
  subdomain?: string;
  primaryColor?: string;
  branchId?: number | string | null;
}

export default function VisualSearchModal({
  subdomain = "nextlook",
  primaryColor = "#059669",
  branchId = null,
}: VisualSearchModalProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"upload" | "camera">("upload");
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [detectedTags, setDetectedTags] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      setErrorMsg("ক্যামেরা অ্যাক্সেস পাওয়া যায়নি। অনুগ্রহ করে ছবি আপলোড করুন।");
      setMode("upload");
    }
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      setMode("upload");
    };
    window.addEventListener("openVisualSearchModal", handleOpen);
    return () => {
      window.removeEventListener("openVisualSearchModal", handleOpen);
      stopCamera();
    };
  }, [stopCamera]);

  useEffect(() => {
    if (isOpen && mode === "camera") {
      startCamera();
    } else {
      stopCamera();
    }
  }, [isOpen, mode, startCamera, stopCamera]);

  const getApiBase = () => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000`;
      }
    }
    return "";
  };

  const runVisualSearch = async (fileOrBlob: File | Blob, dataUrl?: string) => {
    setLoading(true);
    setErrorMsg("");
    if (dataUrl) setPreviewImg(dataUrl);

    try {
      const base = getApiBase();
      const formData = new FormData();
      formData.append("image", fileOrBlob, "visual_query.jpg");
      formData.append("subdomain", subdomain);
      if (branchId) formData.append("branch_id", String(branchId));

      const res = await fetch(
        `${base}/api/v1/ai/visual-search/?subdomain=${encodeURIComponent(subdomain)}`,
        {
          method: "POST",
          headers: {
            "X-Tenant-Domain": subdomain,
            "X-Tenant-Subdomain": subdomain,
            ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
          },
          body: formData,
        }
      );

      if (res.ok) {
        const data = await res.json();
        const items = data.results || data.products || (Array.isArray(data) ? data : []);
        setResults(items);
        setDetectedTags(data.detected_labels || data.tags || ["AI Visual Match", "Similar Style"]);
        if (items.length === 0) {
          await fallbackCatalogSearch();
        }
      } else {
        await fallbackCatalogSearch();
      }
    } catch {
      await fallbackCatalogSearch();
    } finally {
      setLoading(false);
    }
  };

  const fallbackCatalogSearch = async () => {
    try {
      const base = getApiBase();
      const res = await fetch(
        `${base}/api/v1/products/?subdomain=${encodeURIComponent(subdomain)}${
          branchId ? `&branch_id=${branchId}` : ""
        }`,
        { headers: { Accept: "application/json", "X-Tenant-Domain": subdomain } }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setResults(list.slice(0, 8));
        setDetectedTags(["Visual Catalog Match", "Top Recommended"]);
      }
    } catch {
      setErrorMsg("সার্ভারের সাথে সংযোগ করা যায়নি।");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      runVisualSearch(file, reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopCamera();
    setMode("upload");
    canvas.toBlob((blob) => {
      if (blob) runVisualSearch(blob, dataUrl);
    }, "image/jpeg");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-5 bg-slate-950/75 backdrop-blur-md animate-in fade-in select-none font-sans">
      <div className="w-full max-w-3xl max-h-[90vh] rounded-3xl bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden text-slate-900">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center gap-2.5">
            <div
              style={{ backgroundColor: primaryColor }}
              className="w-9 h-9 rounded-2xl text-white flex items-center justify-center shadow-md"
            >
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-black text-slate-900">
                  AI Visual Search (ছবি দিয়ে পণ্য খুঁজুন)
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-black uppercase font-mono">
                  AI Vision
                </span>
              </div>
              <p className="text-[11px] text-slate-500">
                যেকোনো পণ্যের ছবি আপলোড করুন অথবা ক্যামেরা দিয়ে ছবি তুলে স্টোরের মিল থাকা পণ্য খুঁজুন
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              stopCamera();
              setIsOpen(false);
            }}
            className="p-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-slate-100 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          {/* Mode Switcher */}
          <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-2xl w-fit">
            <button
              type="button"
              onClick={() => setMode("upload")}
              style={mode === "upload" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
              className={`px-4 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer ${
                mode === "upload" ? "shadow-sm" : "text-slate-600"
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Upload Photo</span>
            </button>
            <button
              type="button"
              onClick={() => setMode("camera")}
              style={mode === "camera" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
              className={`px-4 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer ${
                mode === "camera" ? "shadow-sm" : "text-slate-600"
              }`}
            >
              <Camera className="w-3.5 h-3.5" />
              <span>Live Camera</span>
            </button>
          </div>

          {errorMsg && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 text-xs font-bold">
              {errorMsg}
            </div>
          )}

          {mode === "camera" ? (
            <div className="relative rounded-3xl overflow-hidden bg-slate-950 aspect-video flex flex-col items-center justify-center border-2 border-slate-800">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute bottom-4 inset-x-0 flex justify-center">
                <button
                  type="button"
                  onClick={captureFromCamera}
                  style={{ backgroundColor: primaryColor }}
                  className="px-6 py-2.5 rounded-full text-white font-black text-xs uppercase tracking-wider shadow-xl flex items-center gap-2 cursor-pointer active:scale-95"
                >
                  <Camera className="w-4 h-4" />
                  <span>Capture & Search</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="sm:col-span-7 border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-3xl p-6 text-center cursor-pointer transition-all bg-slate-50/60 hover:bg-emerald-50/30 flex flex-col items-center justify-center min-h-[160px]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div
                  style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-2"
                >
                  <ImageIcon className="w-6 h-6" />
                </div>
                <p className="text-xs font-black text-slate-800">
                  এখানে ক্লিক করে পণ্যের ছবি আপলোড করুন
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  JPG, PNG বা WebP সাপোর্ট করে
                </p>
              </div>

              <div className="sm:col-span-5 flex flex-col items-center justify-center">
                {previewImg ? (
                  <div className="relative w-full h-40 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
                    <img
                      src={previewImg}
                      alt="Query Preview"
                      className="w-full h-full object-contain"
                    />
                    {loading && (
                      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs flex flex-col items-center justify-center text-white gap-2">
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                        <span className="text-[10px] font-black uppercase tracking-wider">
                          AI Scanning...
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-40 rounded-2xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400 text-xs font-bold gap-1.5">
                    <Sparkles className="w-5 h-5 text-slate-300" />
                    <span>No Image Selected</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detected Tags */}
          {detectedTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-black uppercase text-slate-400 mr-1">
                AI Tags:
              </span>
              {detectedTags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Results Grid */}
          {results.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Matched Products ({results.length})</span>
                </h4>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {results.map((item: any, idx: number) => {
                  const img =
                    item.primary_image ||
                    item.image ||
                    (Array.isArray(item.images) && item.images[0]?.image) ||
                    "/logo.png";
                  const price = Number(item.discount_price || item.price || 0);
                  return (
                    <div
                      key={item.id || idx}
                      onClick={() => {
                        setIsOpen(false);
                        router.push(`/products/${item.slug || item.id}`);
                      }}
                      className="group rounded-2xl border border-slate-200 hover:border-emerald-500 bg-white p-2.5 shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div className="relative w-full h-28 rounded-xl bg-slate-50 overflow-hidden mb-2 flex items-center justify-center">
                        <img
                          src={img}
                          alt={item.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div>
                        <h5 className="text-xs font-black text-slate-800 truncate">
                          {item.name}
                        </h5>
                        <div className="flex items-center justify-between mt-1">
                          <span
                            style={{ color: primaryColor }}
                            className="text-xs font-black font-mono"
                          >
                            ৳{price.toLocaleString()}
                          </span>
                          <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-0.5">
                            View <ArrowRight className="w-2.5 h-2.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
